import React from 'react';
import { observer } from 'mobx-react-lite';
import { MaterialReactTable, type MRT_ColumnDef } from 'material-react-table';
import { Grid, Card, CardContent, Typography, LinearProgress, Box, Chip } from '@mui/material';
import { MemoryIcon as Memory, SpeedIcon as Speed } from "../../../icons";
import { useRootStore } from '../../../store/contexts';
import type { PerformanceEntry as PerformanceEntryType } from './types';

export const PerformanceTab: React.FC = observer(() => {
  const rootStore = useRootStore();
  const monitorStore = rootStore.systemMonitorStore;
  const langStore = rootStore.langStore;

  const webVitals = monitorStore.webVitals;

  const performanceEntries: PerformanceEntryType[] = [
      {
        name: `${langStore.monitorLargestContentfulPaint} (${langStore.monitorLCP})`,
        value: webVitals.LCP,
        rating: webVitals.LCP < 2500 ? 'good' : webVitals.LCP < 4000 ? 'needs-improvement' : 'poor',
        threshold: { good: 2500, poor: 4000 },
      },
      {
        name: `${langStore.monitorCumulativeLayoutShift} (${langStore.monitorCLS})`,
        value: webVitals.CLS,
        rating: webVitals.CLS < 0.1 ? 'good' : webVitals.CLS < 0.25 ? 'needs-improvement' : 'poor',
        threshold: { good: 0.1, poor: 0.25 },
      },
      {
        name: `${langStore.monitorFirstInputDelay} (${langStore.monitorFID})`,
        value: webVitals.FID,
        rating: webVitals.FID < 100 ? 'good' : webVitals.FID < 300 ? 'needs-improvement' : 'poor',
        threshold: { good: 100, poor: 300 },
      },
      {
        name: `${langStore.monitorInteractionToNextPaint} (${langStore.monitorINP})`,
        value: webVitals.INP,
        rating: webVitals.INP < 200 ? 'good' : webVitals.INP < 500 ? 'needs-improvement' : 'poor',
        threshold: { good: 200, poor: 500 },
      },
      {
        name: `${langStore.monitorTimeToFirstByte} (${langStore.monitorTTFB})`,
        value: webVitals.TTFB,
        rating: webVitals.TTFB < 800 ? 'good' : webVitals.TTFB < 1800 ? 'needs-improvement' : 'poor',
        threshold: { good: 800, poor: 1800 },
      },
    ];

  const columns: MRT_ColumnDef<PerformanceEntryType>[] = [
      {
        accessorKey: 'name',
        header: langStore.monitorMetric,
        size: 300,
      },
      {
        accessorKey: 'value',
        header: langStore.monitorValue,
        size: 150,
        Cell: ({ cell, row }) => {
          const value = cell.getValue<number>();
          const name = row.original.name;
          const formatValue = name.includes(langStore.monitorCLS)
            ? value.toFixed(3)
            : `${Math.round(value)}${langStore.monitorMs}`;
          return <Typography variant="body2">{formatValue}</Typography>;
        },
      },
      {
        accessorKey: 'rating',
        header: langStore.monitorRating,
        size: 150,
        Cell: ({ cell }) => {
          const rating = cell.getValue<string>();
          const color = rating === 'good' ? 'success' : rating === 'needs-improvement' ? 'warning' : 'error';
          const label = rating === 'good' ? langStore.monitorGood : rating === 'needs-improvement' ? langStore.monitorNeedsImprovement : langStore.monitorPoor;
          return <Chip label={label.toUpperCase()} color={color} size="small" />;
        },
      },
      {
        accessorKey: 'threshold',
        header: langStore.monitorThreshold,
        size: 200,
        Cell: ({ cell }) => {
          const threshold = cell.getValue<{ good: number; poor: number }>();
          return (
            <Typography variant="caption" color="text.secondary">
              {langStore.monitorGoodThreshold.replace('{value}', threshold.good.toString())} | {langStore.monitorPoorThreshold.replace('{value}', threshold.poor.toString())}
            </Typography>
          );
        },
      },
    ];

  const memory = (performance as any).memory;
  const hasMemory = !!memory;

  const formatBytes = (bytes: number): string => {
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(2)} MB`;
  };

  return (
    <Box sx={{ p: 2 }}>
      <Grid container spacing={2} sx={{ mb: 2 }}>
        <Grid item xs={12} md={6}>
          <Card>
            <CardContent>
              <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                <Speed color="primary" sx={{ mr: 1 }} />
                <Typography variant="h6">{langStore.monitorPerformanceScore}</Typography>
              </Box>
              <Typography variant="h3" color="primary" sx={{ mb: 1 }}>
                {monitorStore.performanceScore}
              </Typography>
              <LinearProgress
                variant="determinate"
                value={monitorStore.performanceScore}
                color={monitorStore.performanceScore >= 80 ? 'success' : monitorStore.performanceScore >= 50 ? 'warning' : 'error'}
              />
            </CardContent>
          </Card>
        </Grid>

        {hasMemory && (
          <Grid item xs={12} md={6}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Memory color="primary" sx={{ mr: 1 }} />
                  <Typography variant="h6">{langStore.monitorMemoryUsage}</Typography>
                </Box>
                <Typography variant="body2" color="text.secondary">
                  {langStore.monitorUsed}: {formatBytes(memory.usedJSHeapSize)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {langStore.monitorTotal}: {formatBytes(memory.totalJSHeapSize)}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {langStore.monitorLimit}: {formatBytes(memory.jsHeapSizeLimit)}
                </Typography>
                <LinearProgress
                  variant="determinate"
                  value={(memory.usedJSHeapSize / memory.jsHeapSizeLimit) * 100}
                  sx={{ mt: 1 }}
                />
              </CardContent>
            </Card>
          </Grid>
        )}
      </Grid>

      <MaterialReactTable
        columns={columns}
        data={performanceEntries}
        enableColumnResizing={false}
        enableColumnOrdering={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enablePagination={false}
        enableSorting={false}
        enableTopToolbar={false}
        muiTableProps={{
          sx: {
            tableLayout: 'fixed',
          },
        }}
        muiTableBodyCellProps={{
          sx: {
            fontSize: '0.75rem',
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            fontSize: '0.75rem',
            fontWeight: 600,
          },
        }}
      />
    </Box>
  );
});
